import { useSetAtom } from 'jotai';
import { useCallback,useState } from 'react';

import type { IInventoryItem } from '@ekhodealer/ekho-common';

import { clearInventorySelectionAtom } from '../../atoms/inventorySelectionAtoms';
import { useSyncSelectedInventory } from '../../hooks/useSyncSelectedInventory';

interface UseSyncActionsParams {
	selectedIds: Set<string>;
}

/**
 * Custom hook that provides action handlers for syncing inventory items
 * Manages sync modal state and handles sync confirmation flow
 */
export const useSyncActions = ({ selectedIds }: UseSyncActionsParams) => {
	const { syncItemsByIds } = useSyncSelectedInventory();
	const clearSelection = useSetAtom(clearInventorySelectionAtom);

	// Sync modal state
	const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
	const [syncInitiatedCount, setSyncInitiatedCount] = useState(0);
	const [isSyncing, setIsSyncing] = useState(false);

	// Handle opening sync modal
	const handleOpenSyncModal = useCallback(() => {
		setSyncInitiatedCount(0); // Reset for fresh modal
		setIsSyncing(false); // Reset loading state
		setIsSyncModalOpen(true);
	}, []);

	// Handle sync confirmation - fire-and-forget, show success immediately
	const handleSyncConfirm = useCallback(
		async (readyItems: IInventoryItem[]) => {
			setIsSyncing(true);
			try {
				const count = await syncItemsByIds(selectedIds, readyItems);
				setSyncInitiatedCount(count);
				clearSelection();
			} finally {
				setIsSyncing(false);
			}
		},
		[syncItemsByIds, selectedIds, clearSelection],
	);

	// Handle sync cancel
	const handleSyncCancel = useCallback(() => {
		setIsSyncModalOpen(false);
	}, []);

	return {
		isSyncModalOpen,
		setIsSyncModalOpen,
		syncInitiatedCount,
		isSyncing,
		handleOpenSyncModal,
		handleSyncConfirm,
		handleSyncCancel,
	};
};
